import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { ReactNode } from 'react';
import { cn } from '@/Domain/utils/cn';

interface CollapsibleCardProps {
  icon: ReactNode;
  title: string;
  description?: string;
  open?: boolean;
  onToggle?: () => void;
  children?: ReactNode;
  className?: string;
  testId?: string;
  headerTestId?: string;
  // Link mode: when set, the card is a single-action row — clicking the header calls
  // onActivate, the chevron points right (static), and there is no collapsible body.
  onActivate?: () => void;
}

export const CollapsibleCard = ({
  icon,
  title,
  description,
  open = false,
  onToggle,
  children,
  className,
  testId,
  headerTestId,
  onActivate,
}: CollapsibleCardProps) => {
  const isLink = onActivate !== undefined;

  return (
    <div
      className={cn('overflow-hidden rounded-xl border border-border bg-card', className)}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={isLink ? onActivate : onToggle}
        className="flex w-full cursor-pointer items-center gap-3 p-3 text-left transition-colors hover:bg-accent"
        aria-expanded={isLink ? undefined : open}
        data-testid={headerTestId}
      >
        <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">{title}</p>
          {description && (
            <p
              className="text-xs text-muted-foreground md:line-clamp-1"
              data-testid={testId ? `${testId}-description` : undefined}
            >
              {description}
            </p>
          )}
        </div>
        {isLink ? (
          <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown
            className={cn(
              'size-4 shrink-0 text-muted-foreground transition-transform duration-200',
              open && 'rotate-180',
            )}
          />
        )}
      </button>
      {!isLink && (
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="content"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="px-3 pb-3 pt-2">{children}</div>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
};
